import { useMarketingViewSection } from '../components/MarketingViewsProvider';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LazyReportSection } from '../hooks/useLazySection';
import { Download, Maximize2, Minimize2 } from 'lucide-react';
import type { BingDateGrain } from '../api/bingDashboard';
import { metaDashboardApi, type MetaDashboardOptions, type MetaReportParams, type MetaReportRow, type MetaReportType } from '../api/metaDashboard';
import VooglamMetaDeliveryDashboard from './VooglamMetaDeliveryDashboard';
import { ColumnToggle } from '../components/ColumnToggle';
import { DashboardQueryActions } from '../components/DashboardQueryActions';
import { alignMarketingDateRange, getCurrentCalendarRange, DELIVERY_RANGE_PRESETS, MarketingTimeRangeFilter } from '../components/MarketingTimeRangeFilter';
import { MultiSelect } from '../components/MultiSelect';
import { ReportLabelSelect } from '../components/ReportLabelSelect';
import { AdTextFilterModal, type AdTextFilterRule } from '../components/AdEntitySearchSelect';
import { ZeeloolFacebookAdFilterSelect } from '../components/ZeeloolFacebookAdFilterSelect'
import { SortableHeader, compareSortValues, type SortState } from '../components/SortableTable';
import { TableLoadingCell, TableLoadingOverlay, TableLoadingState } from '../components/TableLoadingOverlay';
import { DimensionHeaderTooltip, TruncatedValueTooltip } from '../components/DimensionHeaderTooltip';
import { MetricDescriptionTooltip } from '../components/MetricDescriptionTooltip';
import { sf, type FieldKey } from '../lib/fieldPolicy';
import useAppStore from '../store/useAppStore';
import { useDownloadCenterStore } from '../store/useDownloadCenterStore';
import { useToastStore } from '../store/useToastStore';

type DatePreset = 'today' | 'yesterday' | '7d' | '14d' | '30d' | 'thisWeek' | 'thisMonth' | 'custom';

interface MetricField {
  key: string;
  label: string;
  fmt: (v: number) => string;
  permKey?: FieldKey;
}

interface DimensionField {
  key: string;
  label: string;
}

interface FilterState {
  target_type: string[];
  new_old_type: string[];
  country_type: string[];
  country: string[];
  campaign_type: string[];
  campaign_type_2: string[];
  product_posi: string[];
  sex_type: string[];
  product_material: string[];
  bid_type: string[];
  material_type: string[];
  price_type: string[];
  product_type: string[];
  new_customer_segment: string[];
  new_customer_segment_detail: string[];
  old_customer_segment: string[];
  old_customer_segment_detail: string[];
  account_name: string[];
  campaign_id: string;
  campaign_name: string;
  adset_name: string;
  ad_name: string;
}

interface AppliedTopState {
  startDate: string;
  endDate: string;
  dateGrain: BingDateGrain;
}

interface SectionDef {
  report: MetaReportType;
  title: string;
  dims: string[];
  hasDetailTabs?: boolean;
}

interface MetaPageOptions {
  dateGrains: Array<{ value: BingDateGrain; label: string }>;
  dimensions: DimensionField[];
  detailFilterOptions: Array<{ key: EnumFilterKey; label: string; options: string[]; width?: number }>;
  detailTextFilters: Array<{ key: TextFilterKey; label: string; width?: number }>;
  detailPresets: Array<{ value: string; label: string; dims: string[]; report: MetaReportType }>;
}

interface TreeMetricAccumulator {
  budget_base: number;
  budget_share: number;
  spend: number;
  purchase_value: number;
  impressions: number;
  clicks: number;
  add_to_cart: number;
  initiate_checkout: number;
  purchase: number;
  complete_registration: number;
}

interface TreeNode {
  id: string;
  depth: number;
  dimKey: string;
  value: string;
  metrics: TreeMetricAccumulator;
  children: TreeNode[];
  childMap: Map<string, TreeNode>;
}

const SUPPORTED_REGIONS = ['us', 'fr', 'de', 'ca', 'uk'];
const NEW_CUSTOMER_REGIONS = ['us', 'fr'];

const DATE_GRAINS: Array<{ value: BingDateGrain; label: string }> = [
  { value: 'day', label: '日' },
  { value: 'week', label: '周' },
  { value: 'month', label: '月' },
  { value: 'year', label: '年' },
];

const DIMENSIONS: DimensionField[] = [
  { key: 'date', label: '日期维度' },
  { key: 'target_type', label: '目标' },
  { key: 'new_old_type', label: '新老客' },
  { key: 'new_customer_type', label: '新客细分' },
  { key: 'country_type', label: '国家' },
  { key: 'country', label: '定位' },
  { key: 'campaign_type', label: '系列类型' },
  { key: 'campaign_type_2', label: '系列类型2' },
  { key: 'product_posi', label: '商品定位' },
  { key: 'sex_type', label: '性别' },
  { key: 'product_material', label: '材质' },
  { key: 'bid_type', label: '出价方式' },
  { key: 'material_type', label: '素材类型' },
  { key: 'price_type', label: '价格带' },
  { key: 'product_type', label: '新品' },
  { key: 'new_customer_segment', label: '首单分层' },
  { key: 'new_customer_segment_detail', label: '首单分层明细' },
  { key: 'old_customer_segment', label: '老客分层' },
  { key: 'old_customer_segment_detail', label: '老客分层明细' },
  { key: 'campaign_id', label: '广告系列id' },
  { key: 'campaign_name', label: '广告系列名称' },
  { key: 'adset_name', label: '广告组名称' },
  { key: 'ad_name', label: '广告名称' },
  { key: 'account_name', label: '广告账户' },
  { key: 'placement', label: '版位' },
  { key: 'age', label: '年龄' },
  { key: 'platform', label: '平台' },
];

const DETAIL_PRESETS: Array<{ value: string; label: string; dims: string[]; report: MetaReportType }> = [
  { value: 'main', label: '明细报表', dims: ['date'], report: 'detail' },
  { value: 'date', label: '明细报表_日期', dims: ['date'], report: 'detail' },
  { value: 'placement', label: '明细报表_版位', dims: ['date', 'placement'], report: 'detail' },
  { value: 'age', label: '明细报表_年龄', dims: ['date', 'age'], report: 'detail' },
  { value: 'platform', label: '明细报表_平台', dims: ['date', 'platform'], report: 'detail' },
];

const SECTIONS: SectionDef[] = [
  { report: 'overview', title: '总览报表', dims: ['date'] },
  { report: 'target', title: '目标报表', dims: ['target_type', 'date'] },
  { report: 'new_old', title: '新老客报表', dims: ['new_old_type', 'date'] },
  { report: 'country', title: '国家报表', dims: ['country_type', 'date'] },
  { report: 'new_customer', title: '新客细分报表', dims: ['new_customer_type', 'date'] },
  { report: 'detail', title: '明细报表', dims: DETAIL_PRESETS[0].dims, hasDetailTabs: true },
];

const EMPTY_FILTERS: FilterState = {
  target_type: [],
  new_old_type: [],
  country_type: [],
  country: [],
  campaign_type: [],
  campaign_type_2: [],
  product_posi: [],
  sex_type: [],
  product_material: [],
  bid_type: [],
  material_type: [],
  price_type: [],
  product_type: [],
  new_customer_segment: [],
  new_customer_segment_detail: [],
  old_customer_segment: [],
  old_customer_segment_detail: [],
  account_name: [],
  campaign_id: '',
  campaign_name: '',
  adset_name: '',
  ad_name: '',
};

type EnumFilterKey = {
  [K in keyof FilterState]: FilterState[K] extends string[] ? K : never;
}[keyof FilterState];

type TextFilterKey = {
  [K in keyof FilterState]: FilterState[K] extends string ? K : never;
}[keyof FilterState];

const DETAIL_FILTER_OPTIONS: Array<{ key: EnumFilterKey; label: string; options: string[]; width?: number }> = [
  { key: 'target_type', label: '目标', options: ['上层-cpm', '上层-vc', '下层', '中层-加购', '中层-注册'] },
  { key: 'new_old_type', label: '新老客', options: ['新客', '老客'] },
  { key: 'country_type', label: '国家', options: ['国家', '非国家'] },
  { key: 'country', label: '定位', options: [] },
  { key: 'campaign_type', label: '系列类型', options: ['老客DPA', '老客WCA', 'DFCA总', 'ASC总', '首单', 'DABA', 'LAL', '小排', '二排', '三排', '四排', '2排', '4排', '其他'] },
  { key: 'campaign_type_2', label: '系列类型2', options: ['DABA', 'DABA+LAL', 'LAL', '其他'] },
  { key: 'product_posi', label: '商品定位', options: ['侧翼', '其他', '增值', '核心'] },
  { key: 'sex_type', label: '性别', options: ['女', '男'] },
  { key: 'product_material', label: '材质', options: ['Acetate', 'Metal', 'Mixed', 'Plastic', 'TR90', 'Titanium', '其他'] },
  { key: 'bid_type', label: '出价方式', options: ['CPA', 'roas', '价值', '其他', '转化'] },
  { key: 'material_type', label: '素材类型', options: ['BC单图', 'BC视频', '其他', '单图', '商品', '手持视频', '目录', '简欧视频', '精品栏-单图', '精品栏-红人视频', '红人视频', '轮播'], width: 180 },
  { key: 'price_type', label: '价格带', options: ['0美金', '3美金', '7美金', 'sale', '其他', '原价', '正价'] },
  { key: 'product_type', label: '新品', options: ['其他', '当月新品', '非当月新品'] },
  { key: 'new_customer_segment', label: '首单分层', options: ['当月注册未下单', '其他', '未知', '加购未下单', '历史注册未下单', '浏览未加购'], width: 180 },
  { key: 'new_customer_segment_detail', label: '首单分层明细', options: ['当月注册未下单用户', '其他', '未知', '加购未下单', '最近90天注册未下单用户', '最近30天注册未下单用户', '26年注册未下单用户', '浏览未加购', '最近60天注册未下单用户', '所有注册未下单用户'], width: 240 },
  { key: 'old_customer_segment', label: '老客分层', options: ['当年新复购-2026', '往年老复购-2026', '其他', '未知'], width: 200 },
  { key: 'old_customer_segment_detail', label: '老客分层明细', options: ['当年新复购-2026-今年_用户分层_A', '当年新复购-2026-今年_用户分层_B', '当年新复购-2026-今年_用户分层_C', '当年新复购-2026-首次下单时间在2026年', '当年新复购-2026-其他', '往年老复购-2026-今年_用户分层_D', '往年老复购-2026-今年_用户分层_E', '往年老复购-2026-今年_用户分层_F', '往年老复购-2026-今年_用户分层_G', '往年老复购-2026-180天购买', '往年老复购-2026-所有已下单用户', '往年老复购-2026-流失用户', '往年老复购-2026-其他', '未知', '当年新复购-2026-当月下单用户', '往年老复购-2026-成熟期用户', '往年老复购-2026-未次下单时间在2024年', '往年老复购-2026-未次下单时间在2025年'], width: 280 },
  { key: 'account_name', label: '广告账户', options: [], width: 220 },
];

const DETAIL_TEXT_FILTERS: Array<{ key: TextFilterKey; label: string; width?: number }> = [
  { key: 'campaign_id', label: '广告系列id', width: 190 },
  { key: 'campaign_name', label: '广告系列名称', width: 220 },
  { key: 'adset_name', label: '广告组名称', width: 220 },
  { key: 'ad_name', label: '广告名称', width: 220 },
];

const UNSUPPORTED_FIELDS_BY_REGION: Partial<Record<string, readonly string[]>> = {
  fr: ['country_type', 'country', 'campaign_type_2', 'product_posi', 'sex_type', 'product_material', 'new_customer_segment', 'new_customer_segment_detail', 'old_customer_segment', 'old_customer_segment_detail'],
  de: ['country', 'campaign_id', 'new_customer_segment', 'new_customer_segment_detail', 'old_customer_segment', 'old_customer_segment_detail'],
  ca: ['country', 'campaign_id', 'campaign_type_2', 'product_posi', 'sex_type', 'product_material', 'new_customer_segment', 'new_customer_segment_detail', 'old_customer_segment', 'old_customer_segment_detail'],
  uk: ['country', 'campaign_id', 'campaign_type_2', 'product_posi', 'sex_type', 'product_material', 'new_customer_segment', 'new_customer_segment_detail', 'old_customer_segment', 'old_customer_segment_detail'],
};

const DEFAULT_META_OPTIONS: MetaPageOptions = {
  dateGrains: DATE_GRAINS,
  dimensions: DIMENSIONS,
  detailFilterOptions: DETAIL_FILTER_OPTIONS,
  detailTextFilters: DETAIL_TEXT_FILTERS,
  detailPresets: DETAIL_PRESETS,
};

function isDateGrain(value: string): value is BingDateGrain {
  return ['day', 'week', 'month', 'year'].includes(value);
}

function normalizeMetaOptions(options: MetaDashboardOptions | null): MetaPageOptions {
  if (!options) return DEFAULT_META_OPTIONS;
  const apiDetailFilterOptions = options.detail_filter_options as Array<{ key: EnumFilterKey; label: string; options: string[]; width?: number }> | undefined;
  return {
    dateGrains: options.date_grains
      ?.filter(item => isDateGrain(item.value))
      .map(item => ({ value: item.value as BingDateGrain, label: item.label })) ?? DATE_GRAINS,
    dimensions: options.dimensions?.map(item => ({ key: item.value, label: item.label })) ?? DIMENSIONS,
    detailFilterOptions: DETAIL_FILTER_OPTIONS.map(
      fallback => apiDetailFilterOptions?.find(item => item.key === fallback.key) ?? fallback,
    ),
    detailTextFilters: (options.text_filters as Array<{ key: TextFilterKey; label: string; width?: number }> | undefined) ?? DETAIL_TEXT_FILTERS,
    detailPresets: options.presets ?? DETAIL_PRESETS,
  };
}

const fmtNum = (v: number, d = 2) => (v == null ? '-' : v.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d }));
const fmtInt = (v: number) => (v == null ? '-' : Math.round(v).toLocaleString('en-US'));
const fmtPct = (v: number) => (v == null ? '-' : `${(v * 100).toFixed(2)}%`);

const METRICS: MetricField[] = [
  { key: 'budget_share', label: '预算占比', fmt: fmtPct },
  { key: 'impressions', label: '展示次数', fmt: fmtInt },
  { key: 'cpm', label: '账户CPM', fmt: fmtNum, permKey: 'cpc' },
  { key: 'clicks', label: '点击次数', fmt: fmtInt },
  { key: 'ctr', label: '账户CTR', fmt: fmtPct },
  { key: 'cpc', label: '账户CPC', fmt: fmtNum, permKey: 'cpc' },
  { key: 'spend', label: '账户花费', fmt: fmtNum, permKey: 'spend' },
  { key: 'add_to_cart', label: '加购次数', fmt: fmtNum },
  { key: 'cost_per_add_to_cart', label: '单次加购费用', fmt: fmtNum, permKey: 'cpa' },
  { key: 'initiate_checkout', label: '发起结账次数', fmt: fmtInt },
  { key: 'cost_per_checkout', label: '单次结账费用', fmt: fmtNum, permKey: 'cpa' },
  { key: 'purchase', label: '购物次数', fmt: fmtNum },
  { key: 'cpa', label: '账户CPA', fmt: fmtNum, permKey: 'cpa' },
  { key: 'purchase_value', label: '购物转化价值', fmt: fmtNum, permKey: 'revenue' },
  { key: 'account_roi', label: '账户ROI', fmt: fmtNum, permKey: 'roas' },
  { key: 'aov', label: '账户单均价', fmt: fmtNum, permKey: 'revenue' },
  { key: 'click_add_to_cart_rate', label: '点击加购率', fmt: fmtPct },
  { key: 'purchase_conversion_rate', label: '购物车转化率', fmt: fmtPct },
  { key: 'click_purchase_rate', label: '点击购买率', fmt: fmtPct },
  { key: 'checkout_add_to_cart_rate', label: '加购结账率', fmt: fmtPct },
  { key: 'checkout_purchase_rate', label: '结账购买率', fmt: fmtPct },
  { key: 'complete_registration', label: '完成注册次数', fmt: fmtInt },
  { key: 'cost_per_registration', label: '单次注册费用', fmt: fmtNum, permKey: 'cpa' },
  { key: 'click_registration_rate', label: '点击注册率', fmt: fmtPct },
];

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function getPresetRange(preset: DatePreset, customStart: string, customEnd: string) {
  if (preset === 'custom') return { start: customStart, end: customEnd };
  if (preset === 'thisWeek' || preset === 'thisMonth') return getCurrentCalendarRange(preset, today());
  if (preset === 'today') return { start: today(), end: today() };
  if (preset === 'yesterday') return { start: daysAgo(1), end: daysAgo(1) };
  if (preset === '14d') return { start: daysAgo(13), end: today() };
  if (preset === '30d') return { start: daysAgo(29), end: today() };
  return { start: daysAgo(6), end: today() };
}

function initialAppliedTopState(): AppliedTopState {
  return {
    startDate: daysAgo(6),
    endDate: today(),
    dateGrain: 'day',
  };
}

function regionFromSiteCode(code: string): string {
  return String(code || '').split('-')[0]?.toLowerCase() || '';
}

function cleanParams<T extends Record<string, unknown>>(params: T): T {
  const out: Record<string, unknown> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value !== '' && value != null) out[key] = value;
  });
  return out as T;
}

function dimLabel(key: string, dimensions: DimensionField[] = DIMENSIONS) {
  return DIMENSIONS.find(d => d.key === key)?.label ?? dimensions.find(d => d.key === key)?.label ?? key;
}

function metricAccumulator(): TreeMetricAccumulator {
  return {
    budget_base: 0,
    budget_share: 0,
    spend: 0,
    purchase_value: 0,
    impressions: 0,
    clicks: 0,
    add_to_cart: 0,
    initiate_checkout: 0,
    purchase: 0,
    complete_registration: 0,
  };
}

function addMetrics(target: TreeMetricAccumulator, source: Record<string, number> = {}) {
  target.budget_base += source.budget_base ?? 0;
  target.budget_share += source.budget_share ?? 0;
  target.spend += source.spend ?? 0;
  target.purchase_value += source.purchase_value ?? 0;
  target.impressions += source.impressions ?? 0;
  target.clicks += source.clicks ?? 0;
  target.add_to_cart += source.add_to_cart ?? 0;
  target.initiate_checkout += source.initiate_checkout ?? 0;
  target.purchase += source.purchase ?? 0;
  target.complete_registration += source.complete_registration ?? 0;
}

function metricFromAccumulator(acc: TreeMetricAccumulator, key: string, totalSpend = acc.budget_base) {
  switch (key) {
    case 'budget_share':
      return totalSpend ? acc.spend / totalSpend : acc.budget_share;
    case 'cpm':
      return acc.impressions ? (acc.spend / acc.impressions) * 1000 : 0;
    case 'account_roi':
      return acc.spend ? acc.purchase_value / acc.spend : 0;
    case 'cpa':
      return acc.purchase ? acc.spend / acc.purchase : 0;
    case 'aov':
      return acc.purchase ? acc.purchase_value / acc.purchase : 0;
    case 'ctr':
      return acc.impressions ? acc.clicks / acc.impressions : 0;
    case 'cpc':
      return acc.clicks ? acc.spend / acc.clicks : 0;
    case 'click_purchase_rate':
      return acc.clicks ? acc.purchase / acc.clicks : 0;
    case 'cost_per_add_to_cart':
      return acc.add_to_cart ? acc.spend / acc.add_to_cart : 0;
    case 'click_add_to_cart_rate':
      return acc.clicks ? acc.add_to_cart / acc.clicks : 0;
    case 'cost_per_checkout':
      return acc.initiate_checkout ? acc.spend / acc.initiate_checkout : 0;
    case 'cost_per_registration':
      return acc.complete_registration ? acc.spend / acc.complete_registration : 0;
    case 'click_registration_rate':
      return acc.clicks ? acc.complete_registration / acc.clicks : 0;
    case 'purchase_conversion_rate':
      return acc.add_to_cart ? acc.purchase / acc.add_to_cart : 0;
    case 'checkout_add_to_cart_rate':
      return acc.add_to_cart ? acc.initiate_checkout / acc.add_to_cart : 0;
    case 'checkout_purchase_rate':
      return acc.initiate_checkout ? acc.purchase / acc.initiate_checkout : 0;
    default:
      return acc[key as keyof TreeMetricAccumulator] ?? 0;
  }
}

function rowToAccumulator(row: MetaReportRow | null): TreeMetricAccumulator {
  const acc = metricAccumulator();
  if (row) addMetrics(acc, row.metrics);
  return acc;
}

function buildTree(rows: MetaReportRow[], dims: string[]) {
  const root: TreeNode = { id: 'root', depth: -1, dimKey: '', value: '', metrics: metricAccumulator(), children: [], childMap: new Map() };
  rows.forEach(row => {
    addMetrics(root.metrics, row.metrics);
    let parent = root;
    dims.forEach((dim, depth) => {
      const value = row.dimensions?.[dim] || '-';
      const id = `${parent.id}/${dim}:${value}`;
      let node = parent.childMap.get(id);
      if (!node) {
        node = { id, depth, dimKey: dim, value, metrics: metricAccumulator(), children: [], childMap: new Map() };
        parent.childMap.set(id, node);
        parent.children.push(node);
      }
      addMetrics(node.metrics, row.metrics);
      parent = node;
    });
  });

  return root.children;
}

function visibleTreeRows(nodes: TreeNode[], expanded: Set<string>) {
  const out: TreeNode[] = [];
  const walk = (items: TreeNode[]) => {
    items.forEach(node => {
      out.push(node);
      if (node.children.length > 0 && expanded.has(node.id)) walk(node.children);
    });
  };
  walk(nodes);
  return out;
}

function expandableTreeNodeIDs(nodes: TreeNode[]) {
  const ids: string[] = [];
  const walk = (items: TreeNode[]) => {
    items.forEach(node => {
      if (node.children.length > 0) ids.push(node.id);
      walk(node.children);
    });
  };
  walk(nodes);
  return ids;
}

/** Sort metric values only inside each level so parent/child grouping is never broken. */
function sortTreeSiblings(nodes: TreeNode[], sort: SortState, userLevel: number): TreeNode[] {
  if (!sort) return nodes;
  const metric = METRICS.find(item => item.key === sort.key);
  // Do not expose ordering information for metrics that are masked for this user.
  if (!metric || (metric.permKey && userLevel < 2)) return nodes;

  const ordered = nodes
    .map((node, index) => ({ node, index }))
    .sort((left, right) => {
      const result = compareSortValues(
        metricFromAccumulator(left.node.metrics, metric.key),
        metricFromAccumulator(right.node.metrics, metric.key),
        sort.direction,
      );
      return result === 0 ? left.index - right.index : result;
    });

  return ordered.map(({ node }) => (
    node.children.length === 0
      ? node
      : { ...node, children: sortTreeSiblings(node.children, sort, userLevel) }
  ));
}

function detailReportForDims(_dims: string[]): MetaReportType {
  return 'detail';
}

function normalizeDetailDims(dims: string[]) {
  const normalized = dims.length > 0 ? dims : ['date'];
  const independent = normalized.filter(dim => ['placement', 'age', 'platform'].includes(dim));
  if (independent.length <= 1) return normalized;
  const keep = independent[0];
  return normalized.filter(dim => !['placement', 'age', 'platform'].includes(dim) || dim === keep);
}

function EnumFilter({
  label,
  field,
  fallbackOptions,
  value,
  onChange,
  width = 170,
}: {
  label: string;
  field: string;
  fallbackOptions: string[];
  value: string[];
  onChange: (v: string[]) => void;
  width?: number;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: width }}>
      <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text3)' }}>{label}</label>
      <ReportLabelSelect dataset="meta_delivery" field={field} pageFields={DETAIL_FILTER_OPTIONS.map(item => item.key)} fallbackOptions={fallbackOptions} value={value} onChange={onChange} width={width} />
    </div>
  );
}

function serializeFilters(filters: FilterState) {
  const out: Record<string, string> = {};
  (Object.keys(filters) as Array<keyof FilterState>).forEach(key => {
    const value = filters[key];
    if (Array.isArray(value) && value.length > 0) out[key] = value.join(',');
    if (typeof value === 'string' && value.trim() !== '') out[key] = value.trim();
  });
  return out;
}

function DimensionPicker({ selected, onChange, dimensions, loading = false }: { selected: string[]; onChange: (next: string[]) => void; dimensions: DimensionField[]; loading?: boolean }) {
  return (
    <div className="dimension-select-row" style={{ display: 'flex' }}>
      <span className="dimension-select-label">动态维度</span>
      <MultiSelect
        options={dimensions.map(dim => ({ value: dim.key, label: dim.label }))}
        value={dimensions.filter(dim => selected.includes(dim.key)).map(dim => dim.key)}
        onChange={onChange}
        countLabelFormatter={count => `已选 ${count} 项`}
        tagDisplay
        loading={loading}
        loadingText="加载中..."
        style={{ width: 420 }}
      />
    </div>
  );
}

function MetaSectionTable({
  section,
  baseParams,
  queryVersion,
  hasPendingTopChanges,
  onQueryAll,
  userLevel,
  options,
  optionsLoading,
  unsupportedFieldKeys = [],
}: {
  section: SectionDef;
  baseParams: Omit<MetaReportParams, 'report' | 'dimensions'> | null;
  queryVersion: number;
  hasPendingTopChanges: boolean;
  onQueryAll: () => void;
  userLevel: number;
  options: MetaPageOptions;
  optionsLoading: boolean;
  unsupportedFieldKeys?: readonly string[];
}) {
  const [detailPreset, setDetailPreset] = useState('main');
  const [dims, setDims] = useState<string[]>(section.dims);
  const [detailReport, setDetailReport] = useState<MetaReportType>(section.report);
  const [appliedDims, setAppliedDims] = useState<string[]>(section.dims);
  const [appliedDetailReport, setAppliedDetailReport] = useState<MetaReportType>(section.report);
  const [draftFilters, setDraftFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [campaignNameFilters, setCampaignNameFilters] = useState<AdTextFilterRule[]>([]);
  const [adNameFilters, setAdNameFilters] = useState<AdTextFilterRule[]>([]);
  const [appliedCampaignNameFilters, setAppliedCampaignNameFilters] = useState<AdTextFilterRule[]>([]);
  const [appliedAdNameFilters, setAppliedAdNameFilters] = useState<AdTextFilterRule[]>([]);
  const [appliedTopQueryVersion, setAppliedTopQueryVersion] = useState(queryVersion);
  const [detailQueryVersion, setDetailQueryVersion] = useState(0);
  const [rows, setRows] = useState<MetaReportRow[]>([]);
  const [summary, setSummary] = useState<MetaReportRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exportQuery, setExportQuery] = useState<MetaReportParams | null>(null);
  const [exportDims, setExportDims] = useState<string[]>(section.dims);
  const enqueueReportExport = useDownloadCenterStore(state => state.enqueueReportExport);
  const showToast = useToastStore(state => state.show);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<SortState>(null);
  const [hiddenMetricKeys, setHiddenMetricKeys] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(`meta_dashboard_hidden_cols_${section.report}`);
      if (raw) return new Set(JSON.parse(raw) as string[]);
    } catch {
      // ignore corrupted local preference
    }
    return new Set();
  });

  const activeDims = section.hasDetailTabs ? appliedDims : section.dims;
  const hasExpandControls = section.report !== 'overview';
  const treeRoots = useMemo(
    () => buildTree(rows, activeDims),
    [activeDims, rows],
  );
  const sortedTreeRoots = useMemo(() => sortTreeSiblings(treeRoots, sort, userLevel), [sort, treeRoots, userLevel]);
  const visibleRows = useMemo(() => visibleTreeRows(sortedTreeRoots, expanded), [expanded, sortedTreeRoots]);
  const expandableNodeIDs = useMemo(() => expandableTreeNodeIDs(treeRoots), [treeRoots]);
  const visibleMetrics = useMemo(() => METRICS.filter(metric => !hiddenMetricKeys.has(metric.key)), [hiddenMetricKeys]);
  const metricToggleCols = useMemo(() => METRICS.map(metric => ({ key: metric.key, label: metric.label })), []);
  const availableDetailFilterOptions = useMemo(
    () => options.detailFilterOptions.filter(item => !unsupportedFieldKeys.includes(item.key)),
    [options.detailFilterOptions, unsupportedFieldKeys],
  );
  const availableDetailTextFilters = useMemo(
    () => options.detailTextFilters.filter(item => !unsupportedFieldKeys.includes(item.key)),
    [options.detailTextFilters, unsupportedFieldKeys],
  );
  const availableDimensions = useMemo(
    () => options.dimensions.filter(item => !unsupportedFieldKeys.includes(item.key)),
    [options.dimensions, unsupportedFieldKeys],
  );

  const toggleSort = useCallback((key: string) => {
    setSort(current => {
      if (!current || current.key !== key) return { key, direction: 'asc' };
      if (current.direction === 'asc') return { key, direction: 'desc' };
      return null;
    });
  }, []);
  const currentReportParams = useMemo(() => {
    if (!baseParams) return null;
    return cleanParams({
      ...baseParams,
      ...(section.hasDetailTabs ? serializeFilters(appliedFilters) : {}),
      ...(section.hasDetailTabs && (appliedCampaignNameFilters.length > 0 || appliedAdNameFilters.length > 0) ? { ad_text_filters: JSON.stringify([...appliedCampaignNameFilters, ...appliedAdNameFilters]) } : {}),
      report: section.hasDetailTabs ? appliedDetailReport : section.report,
      dimensions: activeDims.join(','),
    });
  }, [activeDims, appliedAdNameFilters, appliedCampaignNameFilters, appliedDetailReport, appliedFilters, baseParams, section.hasDetailTabs, section.report]);

  const toggleMetricHidden = (key: string) => {
    setSort(current => current?.key === key ? null : current);
    setHiddenMetricKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      try { localStorage.setItem(`meta_dashboard_hidden_cols_${section.report}`, JSON.stringify([...next])); } catch {}
      return next;
    });
  };

  const resetMetricHidden = () => {
    setHiddenMetricKeys(new Set());
    try { localStorage.removeItem(`meta_dashboard_hidden_cols_${section.report}`); } catch {}
  };

  const fetchData = useCallback(async () => {
    if (!currentReportParams) {
      setExportQuery(null);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await metaDashboardApi.getReport(currentReportParams);
      setRows(res.data?.list ?? []);
      setSummary(res.data?.summary ?? null);
      setExpanded(new Set());
      setExportQuery(currentReportParams);
      setExportDims(activeDims);
    } catch (e) {
      setRows([]);
      setSummary(null);
      setExportQuery(null);
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [currentReportParams]);

  useEffect(() => {
    if (appliedTopQueryVersion === queryVersion) return;
    setAppliedDims(dims);
    setAppliedDetailReport(detailReport);
    setAppliedFilters(draftFilters);
    setAppliedCampaignNameFilters(campaignNameFilters);
    setAppliedAdNameFilters(adNameFilters);
    setAppliedTopQueryVersion(queryVersion);
  }, [adNameFilters, appliedTopQueryVersion, campaignNameFilters, detailReport, dims, draftFilters, queryVersion]);

  useEffect(() => {
    if (appliedTopQueryVersion !== queryVersion) return;
    fetchData();
  }, [appliedTopQueryVersion, detailQueryVersion, fetchData, queryVersion]);

  const setEnumDetailFilter = (key: EnumFilterKey, value: string[]) => setDraftFilters(prev => ({ ...prev, [key]: value }));
  const setTextDetailFilter = (key: TextFilterKey, value: string) => setDraftFilters(prev => ({ ...prev, [key]: value }));
  useMarketingViewSection('detail:' + section.report, {
    label: section.title, state: { detailPreset, dims, detailReport, draftFilters, hiddenMetricKeys: [...hiddenMetricKeys], campaignNameFilters, adNameFilters },
    restore: value => { setDetailPreset(value.detailPreset); setDims(value.dims); setDetailReport(value.detailReport); setDraftFilters(value.draftFilters); setHiddenMetricKeys(new Set(value.hiddenMetricKeys)); setCampaignNameFilters(value.campaignNameFilters); setAdNameFilters(value.adNameFilters); },
    loading: loading,
  });

  const queryDetail = () => {
    setAppliedFilters(draftFilters);
    setAppliedDims(dims);
    setAppliedDetailReport(detailReport);
    setAppliedCampaignNameFilters(campaignNameFilters);
    setAppliedAdNameFilters(adNameFilters);
    if (hasPendingTopChanges) onQueryAll();
    else setDetailQueryVersion(version => version + 1);
  };
  const resetDetail = () => {
    setDetailPreset('main');
    setDims(section.dims); setAppliedDims(section.dims);
    setDetailReport(section.report); setAppliedDetailReport(section.report);
    setDraftFilters(EMPTY_FILTERS); setAppliedFilters(EMPTY_FILTERS);
    setCampaignNameFilters([]); setAppliedCampaignNameFilters([]);
    setAdNameFilters([]); setAppliedAdNameFilters([]);
    setDetailQueryVersion(version => version + 1);
  };
  const applyDetailDimensions = (next: string[]) => {
    const normalized = normalizeDetailDims(next);
    const nextReport = detailReportForDims(normalized);
    setDims(normalized); setAppliedDims(normalized);
    setDetailReport(nextReport); setAppliedDetailReport(nextReport);
  };
  const renderMetric = (acc: TreeMetricAccumulator, metric: MetricField) => {
    const raw = metric.fmt(metricFromAccumulator(acc, metric.key));
    if (!metric.permKey) return raw;
    return sf(raw, metric.permKey, userLevel).value ?? '锁定';
  };

  const renderSummaryMetric = (metric: MetricField) => {
    const acc = rowToAccumulator(summary);
    return renderMetric(acc, metric);
  };

  const handleExport = useCallback(async () => {
    if (!exportQuery) return;
    try {
      await enqueueReportExport({
        report_key: 'ad_dashboard_meta_report',
        title: `Meta投放看板-${section.title}`,
        file_name: `Meta投放看板_${section.title}_${exportQuery.start_date}_${exportQuery.end_date}.csv`,
        brand_id: exportQuery.brand_id,
        site_id: exportQuery.site_id,
        params: { ...exportQuery },
        columns: [
          ...exportDims.map(dim => ({ key: dim, label: dimLabel(dim, options.dimensions) })),
          ...visibleMetrics.map(metric => ({ key: metric.key, label: metric.label })),
        ],
      });
      showToast('导出任务已提交，请到右上角下载中心查看', 'success');
    } catch (e) {
      showToast(e instanceof Error ? e.message : '导出任务提交失败', 'error');
    }
  }, [enqueueReportExport, exportDims, exportQuery, options.dimensions, section.title, showToast, visibleMetrics]);

  const toggleNode = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const expandAll = () => setExpanded(new Set(expandableNodeIDs));
  const collapseAll = () => setExpanded(new Set());

  return (
    <section style={{
      marginTop: 16,
      background: 'var(--surface)',
      border: '1px solid var(--border2)',
      borderRadius: 'var(--radius-sm)',
      overflow: 'hidden',
      boxShadow: '0 1px 2px rgba(15,23,42,.04)',
    }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border2)', display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{section.title}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          {section.report !== 'overview' && <>
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={expandAll}
              disabled={loading || expandableNodeIDs.length === 0}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <Maximize2 size={14} strokeWidth={2} />
              全部展开
            </button>
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={collapseAll}
              disabled={loading || expanded.size === 0}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <Minimize2 size={14} strokeWidth={2} />
              全部收起
            </button>
          </>}
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={handleExport}
            disabled={loading || !exportQuery}
            title="导出当前报表"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Download size={14} strokeWidth={2} />
            导出
          </button>
          <ColumnToggle cols={metricToggleCols} hiddenKeys={hiddenMetricKeys} onToggle={toggleMetricHidden} onReset={resetMetricHidden} />
        </div>
      </div>

      {section.hasDetailTabs && (
        <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12, borderBottom: '1px solid var(--border2)' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
            {availableDetailFilterOptions.map(item => (
              <EnumFilter
                key={item.key}
                label={item.label}
                field={item.key}
                fallbackOptions={item.options}
                value={draftFilters[item.key]}
                width={item.width}
                onChange={v => setEnumDetailFilter(item.key, v)}
              />
            ))}
            {availableDetailTextFilters.map(item => (
              <ZeeloolFacebookAdFilterSelect
                key={item.key}
                label={item.label}
                field={item.key}
                value={draftFilters[item.key]}
                width={item.width}
                onConfirm={v => setTextDetailFilter(item.key, v)}
              />
            ))}
            <AdTextFilterModal title="广告系列名称组合筛选" field="campaign_name" value={campaignNameFilters} onConfirm={setCampaignNameFilters} />
            <AdTextFilterModal title="广告名称组合筛选" field="ad_name" value={adNameFilters} onConfirm={setAdNameFilters} />
            <DashboardQueryActions loading={loading} onQuery={queryDetail} onReset={resetDetail} queryLabel="查询" />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div className="page-tabs" style={{ marginBottom: 0 }}>
              {options.detailPresets.map(item => (
                <div
                  key={item.value}
                  className={`page-tab${detailPreset === item.value ? ' active' : ''}`}
                  onClick={() => {
                    setDetailPreset(item.value);
                    applyDetailDimensions(item.dims);
                  }}
                >
                  {item.label}
                </div>
              ))}
            </div>
            <DimensionPicker
              selected={dims}
              dimensions={availableDimensions}
              loading={optionsLoading}
              onChange={next => {
                applyDetailDimensions(next);
              }}
            />
          </div>
        </div>
      )}

      {error && <div style={{ margin: '12px 16px 0', color: '#dc2626', fontSize: 13 }}>{error}</div>}

      <div className="table-wrap" style={{ position: 'relative', maxHeight: section.hasDetailTabs ? 620 : 420, overflow: 'auto', borderTop: section.hasDetailTabs ? 0 : '1px solid var(--border2)' }}>
        <table style={{ minWidth: Math.max(1280, 360 + visibleMetrics.length * 112) }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
            <tr>
              <th style={hasExpandControls ? { position: 'sticky', left: 0, zIndex: 4, background: 'var(--surface2)', width: 360, minWidth: 360, maxWidth: 360, boxShadow: '2px 0 4px rgba(15,23,42,.06)' } : { whiteSpace: 'nowrap', background: 'var(--surface2)', minWidth: 240 }}>
                {hasExpandControls ? <DimensionHeaderTooltip labels={(section.hasDetailTabs ? options.dimensions.map(dim => dim.key) : activeDims).map(dim => dimLabel(dim, options.dimensions))} /> : '汇总'}
              </th>
              {visibleMetrics.map(metric => (
                <SortableHeader
                  key={metric.key}
                  sortKey={metric.key}
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                  disabled={Boolean(metric.permKey && userLevel < 2)}
                  style={{ minWidth: 112, whiteSpace: 'nowrap', background: 'var(--surface2)' }}
                >
                  <MetricDescriptionTooltip label={metric.label} />
                </SortableHeader>
              ))}
            </tr>
          </thead>
          <tbody>
            {summary && (
              <tr style={{ fontWeight: 700, background: 'var(--surface)' }}>
                <td style={hasExpandControls ? { position: 'sticky', left: 0, zIndex: 2, background: 'var(--surface)', width: 360, minWidth: 360, maxWidth: 360, boxShadow: '2px 0 4px rgba(15,23,42,.06)', whiteSpace: 'nowrap', color: 'var(--text2)' } : { whiteSpace: 'nowrap', color: 'var(--text2)' }}>总计</td>
                {visibleMetrics.map(metric => (
                  <td key={metric.key} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{renderSummaryMetric(metric)}</td>
                ))}
              </tr>
            )}
            {loading && visibleRows.length === 0 ? (
              <tr><td colSpan={99}><TableLoadingCell /></td></tr>
            ) : !baseParams ? (
              <tr><td colSpan={99} style={{ textAlign: 'center', padding: 42, color: 'var(--text3)' }}>请在左侧切换到支持的 Meta 站点</td></tr>
            ) : visibleRows.length === 0 ? (
              <tr><td colSpan={99} style={{ textAlign: 'center', padding: 42, color: 'var(--text3)' }}>暂无数据</td></tr>
            ) : visibleRows.map((node, index) => (
              <tr key={node.id} className={node.children.length > 0 ? 'tree-row-parent' : undefined}>
                <td style={hasExpandControls ? { position: 'sticky', left: 0, zIndex: 1, background: index % 2 === 0 ? 'var(--surface2)' : 'var(--surface)', boxShadow: '2px 0 4px rgba(15,23,42,.06)', width: 360, minWidth: 360, maxWidth: 360 } : { minWidth: 240, whiteSpace: node.dimKey === 'campaign_name' ? 'normal' : 'nowrap' }}>
                  <span className="tree-cell-content" style={{ paddingLeft: node.depth * 20 }}>
                    {node.children.length > 0 ? (
                      <button
                        type="button"
                        className="tree-toggle-btn"
                        onClick={() => toggleNode(node.id)}
                        aria-label={expanded.has(node.id) ? '收起' : '展开'}
                        aria-expanded={expanded.has(node.id)}
                      >
                        {expanded.has(node.id) ? '−' : '+'}
                      </button>
                    ) : null}
                    {(node.children.length > 0 || node.depth > 0) && (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        height: 20,
                        padding: '0 6px',
                        borderRadius: 'var(--radius-xs)',
                        background: 'var(--primary-bg)',
                        color: 'var(--primary)',
                        fontSize: 11,
                        fontWeight: 600,
                        flexShrink: 0,
                      }}>
                        {dimLabel(node.dimKey, options.dimensions).replace(/维度$/, '')}
                      </span>
                    )}
                    {hasExpandControls ? <TruncatedValueTooltip value={node.value} /> : <span>{node.value}</span>}
                  </span>
                </td>
                {visibleMetrics.map(metric => (
                  <td key={metric.key} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {renderMetric(node.metrics, metric)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {loading && visibleRows.length > 0 && <TableLoadingOverlay />}
      </div>
    </section>
  );
}

function ZeeloolMetaDeliveryDashboard() {
  const { brand, brandId, siteId, sites, adContext, sitesLoaded } = useAppStore();
  const userLevel = adContext?.data_level ?? 1;
  const [dateGrain, setDateGrain] = useState<BingDateGrain>('day');
  const [datePreset, setDatePreset] = useState<DatePreset>('7d');
  const [customStart, setCustomStart] = useState(() => daysAgo(7));
  const [customEnd, setCustomEnd] = useState(() => today());
  const [appliedTop, setAppliedTop] = useState<AppliedTopState>(() => initialAppliedTopState());
  const [queryVersion, setQueryVersion] = useState(0);
  const initialQueryKeyRef = useRef('');
  const [remoteOptions, setRemoteOptions] = useState<MetaDashboardOptions | null>(null);
  const [remoteOptionsLoading, setRemoteOptionsLoading] = useState(true);
  const metaOptions = useMemo(() => normalizeMetaOptions(remoteOptions), [remoteOptions]);

  const currentSite = useMemo(() => {
    if (brand !== 'zeelool') return null;
    const site = sites.find(item => item.id === siteId);
    if (!site) return null;
    const region = regionFromSiteCode(site.code);
    if (!SUPPORTED_REGIONS.includes(region)) return null;
    return { region, siteId: site.id, siteCode: site.code };
  }, [brand, siteId, sites]);

  const dateRange = useMemo(() => alignMarketingDateRange(getPresetRange(datePreset, customStart, customEnd), dateGrain), [customEnd, customStart, dateGrain, datePreset]);
  const baseParams = useMemo(() => {
    if (!brandId || !currentSite?.siteId) return null;
    return cleanParams({
      brand_id: brandId,
      site_id: currentSite.siteId,
      start_date: appliedTop.startDate,
      end_date: appliedTop.endDate,
      date_grain: appliedTop.dateGrain,
    });
  }, [appliedTop, brandId, currentSite?.siteId]);
  const hasPendingTopChanges = dateRange.start !== appliedTop.startDate
    || dateRange.end !== appliedTop.endDate
    || dateGrain !== appliedTop.dateGrain;
  const visibleSections = useMemo(
    () => SECTIONS.filter(section => {
      if (section.report === 'country') return currentSite?.region === 'de';
      if (section.report === 'new_customer') return currentSite && NEW_CUSTOMER_REGIONS.includes(currentSite.region);
      return true;
    }),
    [currentSite],
  );

  useEffect(() => {
    let alive = true;
    setRemoteOptionsLoading(true);
    metaDashboardApi.getOptions()
      .then(res => {
        if (alive) setRemoteOptions(res.data);
      })
      .catch(() => {
        if (alive) setRemoteOptions(null);
      })
      .finally(() => {
        if (alive) setRemoteOptionsLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const query = () => {
    setAppliedTop({ startDate: dateRange.start, endDate: dateRange.end, dateGrain });
    setQueryVersion(v => v + 1);
  };
  useMarketingViewSection('page', {
    label: '公共日期', state: { dateGrain, datePreset, customStart, customEnd },
    restore: value => { setDateGrain(value.dateGrain); setDatePreset(value.datePreset); setCustomStart(value.customStart); setCustomEnd(value.customEnd); },
    loading: false, query: () => { query(); },
    resolve: value => { const range = getPresetRange(value.datePreset, value.customStart, value.customEnd); return { ...value, customStart: range.start, customEnd: range.end }; },
    summary: dateRange.start + ' ～ ' + dateRange.end,
  });

  const reset = () => {
    setDateGrain('day');
    setDatePreset('7d');
    setCustomStart(daysAgo(7));
    setCustomEnd(today());
    const range = getPresetRange('7d', daysAgo(7), today());
    setAppliedTop({ startDate: range.start, endDate: range.end, dateGrain: 'day' });
    setQueryVersion(v => v + 1);
  };
  useEffect(() => {
    if (!brandId || !currentSite?.siteId) return;
    const key = `${brandId}:${currentSite.siteId}`;
    if (initialQueryKeyRef.current === key) return;
    initialQueryKeyRef.current = key;
    setAppliedTop({ startDate: dateRange.start, endDate: dateRange.end, dateGrain });
    setQueryVersion(v => v + 1);
  }, [brandId, currentSite?.siteId, dateGrain, dateRange.end, dateRange.start]);

  if (!sitesLoaded) {
    return (
      <div className="page-section visible">
        <div className="card" style={{ padding: 0 }}><TableLoadingState /></div>
      </div>
    );
  }

  return (
    <div className="page-section visible">
      <div className="dashboard-sticky-filters">
      <div className="data-filter-card">
        <div className="data-filter-row time">
          <MarketingTimeRangeFilter grain={dateGrain} grains={metaOptions.dateGrains} onGrainChange={setDateGrain} preset={datePreset} presets={DELIVERY_RANGE_PRESETS} onPresetChange={setDatePreset} start={customStart} end={customEnd} max={today()} customPreset="custom" onRangeChange={({ start, end }) => { setCustomStart(start); setCustomEnd(end); }} />
          <DashboardQueryActions onReset={reset} onQuery={query} />
        </div>
      </div>
      </div>

      {!currentSite && (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--text2)' }}>
          请在左侧切换到 US / FR / DE / CA / UK 站点后查看 Meta 投放看板。
        </div>
      )}

      {currentSite && visibleSections.map(section => (
        <LazyReportSection key={section.report} enabled={Boolean(currentSite)}>
          <MetaSectionTable
            section={section}
            baseParams={baseParams}
            queryVersion={queryVersion}
            hasPendingTopChanges={hasPendingTopChanges}
            onQueryAll={query}
            userLevel={userLevel}
            options={metaOptions}
            optionsLoading={remoteOptionsLoading}
            unsupportedFieldKeys={UNSUPPORTED_FIELDS_BY_REGION[currentSite.region] ?? []}
          />
        </LazyReportSection>
      ))}
    </div>
  );
}

export default function MetaDeliveryDashboard() {
  const brand = useAppStore(state => state.brand);
  return brand === 'vooglam' ? <VooglamMetaDeliveryDashboard /> : <ZeeloolMetaDeliveryDashboard />;
}
